import { useRecordContext, useTranslate } from "ra-core";
import { DeleteButton } from "@/components/admin/delete-button";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";

import { AsideSection } from "../misc/AsideSection";
import { formatDate } from "../misc/formatDate";
import type { Contact } from "../types";

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <div className="hidden sm:block w-72 min-w-72 text-sm">
      <div className="mb-4 -ml-1">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
      </div>

      <AsideSection title={translate("crm.common.details")}>
        <AsideRow label={translate("resources.contacts.fields.company_id")}>
          <ReferenceField source="company_id" reference="companies" link="show">
            <TextField source="name" />
          </ReferenceField>
        </AsideRow>
        <AsideRow label={translate("resources.contacts.fields.email")}>
          {record.email ? (
            <a
              className="underline hover:no-underline break-all"
              href={`mailto:${record.email}`}
            >
              {record.email}
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </AsideRow>
        {record.created_at ? (
          <AsideRow label={translate("resources.contacts.fields.created_at")}>
            {formatDate(record.created_at)}
          </AsideRow>
        ) : null}
      </AsideSection>

      {link !== "edit" ? null : (
        <div className="mt-6 pt-6 border-t flex flex-col gap-2 items-start">
          <DeleteButton
            className="cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

const AsideRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="break-words">{children}</span>
  </div>
);
